import { createBaseApi } from '../baseApi';

// Create base lookbook API
const lookbookApi = createBaseApi('lookbookApi');

// Enhanced API with lookbook endpoints
export const enhancedLookbookApi = lookbookApi.injectEndpoints({
  endpoints: (builder) => ({
    lookbookPage: builder.query({
      query: (params) => ({ url: '/lookbooks/me', params }),
      providesTags: ['Lookbooks'],
      transformResponse: response => ({ items: response.data || response.results || response.items || [], pagination: response.pagination })
    }),
    lookbookEntryPage: builder.query({
      query: ({ id, ...params }) => ({ url: `/lookbooks/${id}/entries`, params }),
      providesTags: ['Lookbooks', 'LookbookEntries'],
      transformResponse: response => ({ items: (response.data || []).map(result => {
        const last = value => Array.isArray(value) ? value[value.length - 1] : value;
        const resultImage = last(result.resultImage);
        const removedBgImage = last(result.removedBgImage);
        return { ...result, resultImage, removedBgImage,
          imageUrl: (result.useRemovedImage !== false && removedBgImage?.url) || resultImage?.url,
          allImages: (Array.isArray(result.resultImage) ? result.resultImage : [result.resultImage]).map((image, index) => {
            const removed = Array.isArray(result.removedBgImage) ? result.removedBgImage[index] : result.removedBgImage;
            return (result.useRemovedImage !== false && removed?.url) || image?.url;
          }).filter(Boolean).reverse() };
      }), pagination: response.pagination })
    }),

    // Get user's lookbooks with previews
    getLookbooks: builder.query({
      query: () => '/lookbooks/me',
      providesTags: ['Lookbooks'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data.map(lb => ({
            id: lb.id,
            name: lb.name,
            isDefault: lb.isDefault || false,
            previewImage: lb.previewImage,
            previewImages: lb.previewImages || [], // Array of first 4 preview images for card display
            itemCount: lb.itemCount || lb.resultCount,
            resultCount: lb.resultCount, // Keep for backward compatibility
            entries: lb.entries || [], // Include entries for membership checking
            createdAt: lb.createdAt
          }));
        }
        return [];
      }
    }),

    // Get a specific lookbook with VTO results
    getLookbook: builder.query({
      query: (id) => `/lookbooks/${id}`,
      providesTags: (result, error, id) => [{ type: 'Lookbooks', id }],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return null;
      }
    }),

    // Create a new lookbook
    createLookbook: builder.mutation({
      query: ({ name, isPublic }) => ({
        url: '/lookbooks',
        method: 'POST',
        body: { name, isSharedForTryOn: isPublic }
      }),
      invalidatesTags: ['Lookbooks'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        throw new Error(response?.message || 'Failed to create lookbook');
      }
    }),

    // Update lookbook name
    updateLookbook: builder.mutation({
      query: ({ id, name, isPublic }) => ({
        url: `/lookbooks/${id}`,
        method: 'PUT',
        body: { name, isSharedForTryOn: isPublic }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Lookbooks', id },
        'Lookbooks'
      ]
    }),

    // Delete a lookbook
    deleteLookbook: builder.mutation({
      query: (id) => ({
        url: `/lookbooks/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['Lookbooks']
    }),

    // Add VTO results to lookbook
    addResultsToLookbook: builder.mutation({
      query: ({ lookbookId, resultIds }) => ({
        url: `/lookbooks/${lookbookId}/add-results`,
        method: 'POST',
        body: { resultIds }
      }),
      invalidatesTags: (result, error, { lookbookId }) => [
        { type: 'Lookbooks', id: lookbookId },
        'Lookbooks'
      ]
    }),

    // Remove VTO results from lookbook
    removeResultsFromLookbook: builder.mutation({
      query: ({ lookbookId, resultIds }) => ({
        url: `/lookbooks/${lookbookId}/remove-results`,
        method: 'POST',
        body: { resultIds }
      }),
      invalidatesTags: (result, error, { lookbookId }) => [
        { type: 'Lookbooks', id: lookbookId },
        'Lookbooks'
      ]
    }),

    // Add entries to lookbook (for folder management modal)
    addEntriesToLookbook: builder.mutation({
      query: ({ lookbookId, entryIds }) => ({
        url: `/lookbooks/${lookbookId}/add-entries`,
        method: 'POST',
        body: { entryIds }
      }),
      invalidatesTags: (result, error, { lookbookId }) => [
        { type: 'Lookbooks', id: lookbookId },
        { type: 'LookbookEntries', id: lookbookId },
        'Lookbooks'
      ]
    }),

    // Remove entries from lookbook (for folder management modal)
    removeEntriesFromLookbook: builder.mutation({
      query: ({ lookbookId, entryIds }) => ({
        url: `/lookbooks/${lookbookId}/remove-entries`,
        method: 'POST',
        body: { entryIds }
      }),
      invalidatesTags: (result, error, { lookbookId }) => [
        { type: 'Lookbooks', id: lookbookId },
        { type: 'LookbookEntries', id: lookbookId },
        'Lookbooks'
      ]
    }),

    // === Canvas State Entry Endpoints ===

    // Save a canvas state entry to a lookbook
    saveCanvasEntry: builder.mutation({
      query: ({ lookbookId, canvasState, previewImage, name }) => {
        const formData = new FormData();
        formData.append('canvasState', JSON.stringify(canvasState));
        if (name) formData.append('name', name);
        if (previewImage) formData.append('previewImage', previewImage);

        return {
          url: `/lookbooks/${lookbookId}/entries`,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: (result, error, { lookbookId }) => [
        { type: 'Lookbooks', id: lookbookId },
        { type: 'LookbookEntries', id: lookbookId },
        'Lookbooks'
      ]
    }),

    // Get all entries for a lookbook
    getLookbookEntries: builder.query({
      query: (lookbookId) => `/lookbooks/${lookbookId}/entries`,
      providesTags: (result, error, lookbookId) => [
        { type: 'LookbookEntries', id: lookbookId }
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return [];
      }
    }),

    // Get a single entry with full canvas state
    getLookbookEntry: builder.query({
      query: (entryId) => `/lookbook-entries/${entryId}`,
      providesTags: (result, error, entryId) => [
        { type: 'LookbookEntry', id: entryId }
      ],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return null;
      }
    }),

    // Delete a lookbook entry
    deleteLookbookEntry: builder.mutation({
      query: (entryId) => ({
        url: `/lookbook-entries/${entryId}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['LookbookEntries', 'Lookbooks']
    }),

    // Check if a canvas state already exists in any lookbook
    checkExistingCanvasEntry: builder.mutation({
      query: (canvasState) => ({
        url: '/lookbook-entries/check-existing',
        method: 'POST',
        body: { canvasState }
      }),
      transformResponse: (response) => {
        if (response?.success) {
          return response;
        }
        return { success: false, exists: false, data: null };
      }
    })
  })
});

export const {
  useLookbookPageQuery,
  useLookbookEntryPageQuery,
  useGetLookbooksQuery,
  useGetLookbookQuery,
  useCreateLookbookMutation,
  useUpdateLookbookMutation,
  useDeleteLookbookMutation,
  useAddResultsToLookbookMutation,
  useRemoveResultsFromLookbookMutation,
  // Entry management hooks (for folder management modal)
  useAddEntriesToLookbookMutation,
  useRemoveEntriesFromLookbookMutation,
  // Canvas entry hooks
  useSaveCanvasEntryMutation,
  useGetLookbookEntriesQuery,
  useLazyGetLookbookEntriesQuery,
  useGetLookbookEntryQuery,
  useDeleteLookbookEntryMutation,
  useCheckExistingCanvasEntryMutation
} = enhancedLookbookApi;

export default enhancedLookbookApi;
